import { put, call, all, takeEvery } from 'redux-saga/effects'
import { withCallback } from 'redux-saga-callback'
import { store as reduxStore } from 'index'
import { getAppHost } from 'helpers'
import * as customBotsService from 'services/customBots/customBots'
import * as aiBotsService from 'services/customBots/aiBots'
import * as aiInstructionsService from 'services/customBots/aiInstructions'
import {
  ChatApp,
  ApiErrorBody,
  ListMeta,
  CustomBot,
  AiBot,
  AiInstruction,
  CustomBotAddPayload,
  CustomBotUpdatePayload,
  AiBotAddPayload,
  AiBotUpdatePayload,
  AiInstructionAddPayload,
  AiInstructionUpdatePayload,
} from 'services/customBots/types'
import actions from './actions'
import { LoosePagination } from './reducers'

type Saga<P> = { payload: P }
type Write<P> = { id: string; payload: P }
type SagaWorker = (action: any) => any

interface ListPayload {
  data?: ChatApp
  currentPage?: number
  limit?: number
}

interface IdPayload {
  id: string
}

interface StatusPayload {
  id: string
  status?: boolean
}

interface ListEnvelope<T> {
  data?: T[]
  meta?: ListMeta
  error?: ApiErrorBody
}

interface ItemEnvelope<T> {
  data?: T
  error?: ApiErrorBody
}

function appScope() {
  const { active, appSettings } = reduxStore.getState().apps
  return { activeApp: active as ChatApp, apiHost: getAppHost(appSettings) }
}

/** `meta.pagination.total` is the true row count — `total_pages * per_page` overstates it. */
function listTotal<T>(response: ListEnvelope<T>): number {
  const pagination = response?.meta?.pagination
  if (pagination && typeof pagination.total === 'number') return pagination.total
  return response?.data?.length ?? 0
}

function listPagination<T>(response: ListEnvelope<T>): LoosePagination {
  return response?.meta?.pagination ?? {}
}

/** The page is served by two backends with different error bodies: chat-api sends
 * `details` (field -> messages), ai-api puts everything in `message`. */
function notifyApiError(error?: ApiErrorBody, fallback = 'Some error occurred'): void {
  if (!error) {
    window.triggerNotification(fallback, 'error')
    return
  }
  if (error.code === 'ERR_BAD_REQUEST' && error.details) {
    const detailMessages = Object.values(error.details)
      .flat()
      .filter(Boolean)
    if (detailMessages.length > 0) {
      detailMessages.forEach(detail => window.triggerNotification(detail, 'error'))
      return
    }
  }
  if (error.code === 'ERR_PLAN_RESTRICTION') {
    window.triggerNotification('Plan restriction - Feature not available', 'error')
    return
  }
  window.triggerNotification(error.message || fallback, 'error')
}

/** Page to land on after a delete: stay put while the current page still holds
 * more than one row, otherwise fall back to the first. */
function pageAfterDelete(pagination: LoosePagination): number {
  return pagination && (pagination.count ?? 0) > 1 ? pagination.current_page ?? 1 : 1
}

/** Builds the API's `filtered` + `unset` payload split. Only a cleared field is
 * unset, so a `false` or a `0` is sent rather than silently dropped. */
function buildUpdatePayload(payload: Record<string, unknown>): CustomBotUpdatePayload {
  const filtered: Record<string, unknown> = {}
  const unset: string[] = []
  Object.entries(payload || {}).forEach(([key, value]) => {
    const isCleared =
      value === undefined || value === null || (typeof value === 'string' && value.trim() === '')
    if (isCleared) {
      unset.push(key)
    } else {
      filtered[key] = value
    }
  })
  filtered.unset = unset
  return filtered as CustomBotUpdatePayload
}

/* ─────────────── Custom Bots — chat-api /bots ─────────────── */

export function* CUSTOM_BOTS_LIST({ payload = {} }: { payload?: ListPayload }) {
  yield put({
    type: actions.SET_STATE,
    payload: { areCustomBotsLoading: true, customBotsLoadFailed: false },
  })
  try {
    const { activeApp, apiHost } = appScope()
    const app = payload.data ?? activeApp
    const pageNo = payload.currentPage ?? 1

    const response: ListEnvelope<CustomBot> = yield call(
      customBotsService.list,
      app,
      apiHost,
      pageNo,
    )

    if (response && response.data) {
      yield put({
        type: actions.SET_STATE,
        payload: {
          customBotsList: response.data,
          customBotsTotal: listTotal(response),
          customBotsPagination: listPagination(response),
          areCustomBotsLoading: false,
          customBotsLoadFailed: false,
        },
      })
    } else {
      yield put({
        type: actions.SET_STATE,
        payload: {
          customBotsList: [],
          customBotsTotal: 0,
          customBotsPagination: {},
          areCustomBotsLoading: false,
          customBotsLoadFailed: true,
        },
      })
      // The service resolves API errors rather than throwing, so this branch —
      // not `catch` — is where a failed load lands.
      notifyApiError(response?.error, 'Failed to load bots')
    }
  } catch (e) {
    yield put({
      type: actions.SET_STATE,
      payload: {
        customBotsList: [],
        customBotsTotal: 0,
        customBotsPagination: {},
        areCustomBotsLoading: false,
        customBotsLoadFailed: true,
      },
    })
    // Without this a failed load is indistinguishable from having no bots.
    window.triggerNotification('Failed to load bots', 'error')
  }
}

export function* GET_CUSTOM_BOT({ payload }: Saga<IdPayload>) {
  yield put({ type: actions.SET_STATE, payload: { areCustomBotsLoading: true } })
  try {
    const { activeApp, apiHost } = appScope()
    const response: ItemEnvelope<CustomBot> = yield call(
      customBotsService.get,
      payload.id,
      activeApp,
      apiHost,
    )

    if (response && response.data) {
      yield put({
        type: actions.SET_STATE,
        payload: { customBotsActive: response.data, areCustomBotsLoading: false },
      })
    } else {
      yield put({ type: actions.SET_STATE, payload: { areCustomBotsLoading: false } })
      notifyApiError(response?.error, 'Failed to load the bot')
    }
  } catch (e) {
    yield put({ type: actions.SET_STATE, payload: { areCustomBotsLoading: false } })
    window.triggerNotification('Failed to load the bot', 'error')
  }
}

export function* ADD_CUSTOM_BOT({ payload }: Saga<CustomBotAddPayload>) {
  yield put({ type: actions.SET_STATE, payload: { areCustomBotsLoading: true } })
  try {
    const { activeApp, apiHost } = appScope()
    const response: ItemEnvelope<CustomBot> = yield call(
      customBotsService.add,
      payload,
      activeApp,
      apiHost,
    )

    if (response && response.data) {
      yield put({ type: actions.CUSTOM_BOTS_LIST, payload: { currentPage: 1, data: activeApp } })
      window.triggerNotification('Bot added', 'success')
      return response
    }
    yield put({ type: actions.SET_STATE, payload: { areCustomBotsLoading: false } })
    notifyApiError(response?.error)
    return response
  } catch (e) {
    yield put({ type: actions.SET_STATE, payload: { areCustomBotsLoading: false } })
    window.triggerNotification('Some error occurred', 'error')
    return null
  }
}

export function* UPDATE_CUSTOM_BOT({ id, payload }: Write<CustomBotUpdatePayload>) {
  yield put({ type: actions.SET_STATE, payload: { areCustomBotsLoading: true } })
  try {
    const { activeApp, apiHost } = appScope()
    const response: ItemEnvelope<CustomBot> = yield call(
      customBotsService.update,
      id,
      buildUpdatePayload(payload),
      activeApp,
      apiHost,
    )

    if (response && response.data) {
      // The single-bot GET is cached behind the write path and can return the
      // pre-update row, so seed `active` from the update response instead.
      yield put({ type: actions.SET_STATE, payload: { customBotsActive: response.data } })
      yield put({ type: actions.CUSTOM_BOTS_LIST, payload: { currentPage: 1, data: activeApp } })
      window.triggerNotification('Bot saved', 'success')
      return response
    }
    yield put({ type: actions.SET_STATE, payload: { areCustomBotsLoading: false } })
    notifyApiError(response?.error)
    return response
  } catch (e) {
    yield put({ type: actions.SET_STATE, payload: { areCustomBotsLoading: false } })
    window.triggerNotification('Some error occurred', 'error')
    return null
  }
}

export function* DELETE_CUSTOM_BOT({ payload }: Saga<IdPayload>) {
  yield put({ type: actions.SET_STATE, payload: { areCustomBotsLoading: true } })
  try {
    const { activeApp, apiHost } = appScope()
    const { customBotsPagination } = reduxStore.getState().customBots
    const response: ItemEnvelope<unknown> = yield call(
      customBotsService.del,
      payload.id,
      activeApp,
      apiHost,
    )

    if (response && response.data) {
      yield put({
        type: actions.CUSTOM_BOTS_LIST,
        payload: { currentPage: pageAfterDelete(customBotsPagination), data: activeApp },
      })
      window.triggerNotification('Bot deleted', 'success')
      return
    }
    yield put({ type: actions.SET_STATE, payload: { areCustomBotsLoading: false } })
    notifyApiError(response?.error)
  } catch (e) {
    yield put({ type: actions.SET_STATE, payload: { areCustomBotsLoading: false } })
    window.triggerNotification('Some error occurred', 'error')
  }
}

export function* CHANGE_CUSTOM_BOT_STATUS({ payload }: Saga<StatusPayload>) {
  yield put({ type: actions.SET_STATE, payload: { areCustomBotsLoading: true } })
  try {
    const { activeApp, apiHost } = appScope()
    const { customBotsList } = reduxStore.getState().customBots
    // `enabled` is omitted entirely when false, so read it as `?? false`.
    const nextEnabled = !(payload.status ?? false)

    const response: ItemEnvelope<CustomBot> = yield call(
      customBotsService.update,
      payload.id,
      { enabled: nextEnabled },
      activeApp,
      apiHost,
    )

    if (response && response.data) {
      const updatedList = (customBotsList as CustomBot[]).map(bot =>
        bot.uid === payload.id ? { ...bot, enabled: nextEnabled } : bot,
      )
      yield put({
        type: actions.SET_STATE,
        payload: { customBotsList: updatedList, areCustomBotsLoading: false },
      })
      window.triggerNotification(`Bot ${nextEnabled ? 'enabled' : 'disabled'}`, 'success')
      return
    }
    yield put({ type: actions.SET_STATE, payload: { areCustomBotsLoading: false } })
    notifyApiError(response?.error)
  } catch (e) {
    yield put({ type: actions.SET_STATE, payload: { areCustomBotsLoading: false } })
    window.triggerNotification('Some error occurred', 'error')
  }
}

/* ─────────────── AI Bots (Legacy) — ai-api /ai/bots ─────────────── */

export function* AI_BOTS_LIST({ payload = {} }: { payload?: ListPayload }) {
  yield put({
    type: actions.SET_STATE,
    payload: { areAiBotsLoading: true, aiBotsLoadFailed: false },
  })
  try {
    const { activeApp, apiHost } = appScope()
    const app = payload.data ?? activeApp
    const pageNo = payload.currentPage ?? 1

    const response: ListEnvelope<AiBot> = yield call(aiBotsService.list, app, apiHost, pageNo)

    if (response && response.data) {
      yield put({
        type: actions.SET_STATE,
        payload: {
          aiBotsList: response.data,
          aiBotsTotal: listTotal(response),
          aiBotsPagination: listPagination(response),
          areAiBotsLoading: false,
          aiBotsLoadFailed: false,
        },
      })
    } else {
      yield put({
        type: actions.SET_STATE,
        payload: {
          aiBotsList: [],
          aiBotsTotal: 0,
          aiBotsPagination: {},
          areAiBotsLoading: false,
          aiBotsLoadFailed: true,
        },
      })
      notifyApiError(response?.error, 'Failed to load AI bots')
    }
  } catch (e) {
    yield put({
      type: actions.SET_STATE,
      payload: {
        aiBotsList: [],
        aiBotsTotal: 0,
        aiBotsPagination: {},
        areAiBotsLoading: false,
        aiBotsLoadFailed: true,
      },
    })
    window.triggerNotification('Failed to load AI bots', 'error')
  }
}

export function* GET_AI_BOT({ payload }: Saga<IdPayload>) {
  yield put({ type: actions.SET_STATE, payload: { areAiBotsLoading: true } })
  try {
    const { activeApp, apiHost } = appScope()
    const response: ItemEnvelope<AiBot> = yield call(
      aiBotsService.get,
      payload.id,
      activeApp,
      apiHost,
    )

    if (response && response.data) {
      yield put({
        type: actions.SET_STATE,
        payload: { aiBotsActive: response.data, areAiBotsLoading: false },
      })
    } else {
      yield put({ type: actions.SET_STATE, payload: { areAiBotsLoading: false } })
      notifyApiError(response?.error, 'Failed to load the AI bot')
    }
  } catch (e) {
    yield put({ type: actions.SET_STATE, payload: { areAiBotsLoading: false } })
    window.triggerNotification('Failed to load the AI bot', 'error')
  }
}

export function* ADD_AI_BOT({ payload }: Saga<AiBotAddPayload>) {
  yield put({ type: actions.SET_STATE, payload: { areAiBotsLoading: true } })
  try {
    const { activeApp, apiHost } = appScope()
    const response: ItemEnvelope<AiBot> = yield call(aiBotsService.add, payload, activeApp, apiHost)

    if (response && response.data) {
      yield put({ type: actions.AI_BOTS_LIST, payload: { currentPage: 1, data: activeApp } })
      window.triggerNotification('AI Bot added', 'success')
      return response
    }
    yield put({ type: actions.SET_STATE, payload: { areAiBotsLoading: false } })
    notifyApiError(response?.error)
    return response
  } catch (e) {
    yield put({ type: actions.SET_STATE, payload: { areAiBotsLoading: false } })
    window.triggerNotification('Some error occurred', 'error')
    return null
  }
}

export function* UPDATE_AI_BOT({ id, payload }: Write<AiBotUpdatePayload>) {
  yield put({ type: actions.SET_STATE, payload: { areAiBotsLoading: true } })
  try {
    const { activeApp, apiHost } = appScope()
    const response: ItemEnvelope<AiBot> = yield call(
      aiBotsService.update,
      id,
      buildUpdatePayload(payload),
      activeApp,
      apiHost,
    )

    if (response && response.data) {
      // ai-api serves the pre-update bot for a moment after a write, so patch
      // the row from the PUT response instead of refetching.
      const updated = response.data
      const { aiBotsList } = reduxStore.getState().customBots
      yield put({
        type: actions.SET_STATE,
        payload: {
          aiBotsActive: updated,
          aiBotsList: (aiBotsList as AiBot[]).map(bot => (bot.uid === id ? updated : bot)),
          areAiBotsLoading: false,
        },
      })
      window.triggerNotification('AI Bot saved', 'success')
      return response
    }
    yield put({ type: actions.SET_STATE, payload: { areAiBotsLoading: false } })
    notifyApiError(response?.error)
    return response
  } catch (e) {
    yield put({ type: actions.SET_STATE, payload: { areAiBotsLoading: false } })
    window.triggerNotification('Some error occurred', 'error')
    return null
  }
}

export function* DELETE_AI_BOT({ payload }: Saga<IdPayload>) {
  yield put({ type: actions.SET_STATE, payload: { areAiBotsLoading: true } })
  try {
    const { activeApp, apiHost } = appScope()
    const { aiBotsPagination } = reduxStore.getState().customBots
    const response: ItemEnvelope<unknown> = yield call(
      aiBotsService.del,
      payload.id,
      activeApp,
      apiHost,
    )

    if (response && response.data) {
      yield put({
        type: actions.AI_BOTS_LIST,
        payload: { currentPage: pageAfterDelete(aiBotsPagination), data: activeApp },
      })
      window.triggerNotification('AI Bot deleted', 'success')
      return
    }
    yield put({ type: actions.SET_STATE, payload: { areAiBotsLoading: false } })
    notifyApiError(response?.error)
  } catch (e) {
    yield put({ type: actions.SET_STATE, payload: { areAiBotsLoading: false } })
    window.triggerNotification('Some error occurred', 'error')
  }
}

export function* CHANGE_AI_BOT_STATUS({ payload }: Saga<StatusPayload>) {
  yield put({ type: actions.SET_STATE, payload: { areAiBotsLoading: true } })
  try {
    const { activeApp, apiHost } = appScope()
    const { aiBotsList } = reduxStore.getState().customBots
    const nextEnabled = !(payload.status ?? false)

    const response: ItemEnvelope<AiBot> = yield call(
      aiBotsService.update,
      payload.id,
      { enabled: nextEnabled },
      activeApp,
      apiHost,
    )

    if (response && response.data) {
      const updatedList = (aiBotsList as AiBot[]).map(bot =>
        bot.uid === payload.id ? { ...bot, enabled: nextEnabled } : bot,
      )
      yield put({
        type: actions.SET_STATE,
        payload: { aiBotsList: updatedList, areAiBotsLoading: false },
      })
      window.triggerNotification(`AI Bot ${nextEnabled ? 'enabled' : 'disabled'}`, 'success')
      return
    }
    yield put({ type: actions.SET_STATE, payload: { areAiBotsLoading: false } })
    notifyApiError(response?.error)
  } catch (e) {
    yield put({ type: actions.SET_STATE, payload: { areAiBotsLoading: false } })
    window.triggerNotification('Some error occurred', 'error')
  }
}

/* ────────── AI Instructions (Legacy) — ai-api /ai/personalities ────────── */

export function* AI_INSTRUCTIONS_LIST({ payload = {} }: { payload?: ListPayload }) {
  yield put({
    type: actions.SET_STATE,
    payload: { areAiInstructionsLoading: true, aiInstructionsLoadFailed: false },
  })
  try {
    const { activeApp, apiHost } = appScope()
    const app = payload.data ?? activeApp
    const pageNo = payload.currentPage ?? 1

    const response: ListEnvelope<AiInstruction> = yield call(
      aiInstructionsService.list,
      app,
      apiHost,
      pageNo,
      payload.limit,
    )

    if (response && response.data) {
      yield put({
        type: actions.SET_STATE,
        payload: {
          aiInstructionsList: response.data,
          aiInstructionsTotal: listTotal(response),
          aiInstructionsPagination: listPagination(response),
          areAiInstructionsLoading: false,
          aiInstructionsLoadFailed: false,
        },
      })
    } else {
      yield put({
        type: actions.SET_STATE,
        payload: {
          aiInstructionsList: [],
          aiInstructionsTotal: 0,
          aiInstructionsPagination: {},
          areAiInstructionsLoading: false,
          aiInstructionsLoadFailed: true,
        },
      })
      notifyApiError(response?.error, 'Failed to load instructions')
    }
  } catch (e) {
    yield put({
      type: actions.SET_STATE,
      payload: {
        aiInstructionsList: [],
        aiInstructionsTotal: 0,
        aiInstructionsPagination: {},
        areAiInstructionsLoading: false,
        aiInstructionsLoadFailed: true,
      },
    })
    window.triggerNotification('Failed to load instructions', 'error')
  }
}

/** Fills the AI Bot form's dropdown, writing only its own key. */
export function* AI_INSTRUCTION_OPTIONS({ payload = {} }: { payload?: ListPayload }) {
  try {
    const { activeApp, apiHost } = appScope()
    const response: ListEnvelope<AiInstruction> = yield call(
      aiInstructionsService.list,
      payload.data ?? activeApp,
      apiHost,
      1,
      payload.limit,
    )
    if (response && response.data) {
      yield put({ type: actions.SET_STATE, payload: { aiInstructionOptions: response.data } })
    } else {
      yield put({ type: actions.SET_STATE, payload: { aiInstructionOptions: [] } })
      // Otherwise the form's required Instruction dropdown is empty with no reason given.
      notifyApiError(response?.error, 'Failed to load instructions')
    }
  } catch (e) {
    yield put({ type: actions.SET_STATE, payload: { aiInstructionOptions: [] } })
  }
}

export function* GET_AI_INSTRUCTION({ payload }: Saga<IdPayload>) {
  yield put({ type: actions.SET_STATE, payload: { areAiInstructionsLoading: true } })
  try {
    const { activeApp, apiHost } = appScope()
    const response: ItemEnvelope<AiInstruction> = yield call(
      aiInstructionsService.get,
      payload.id,
      activeApp,
      apiHost,
    )

    if (response && response.data) {
      yield put({
        type: actions.SET_STATE,
        payload: { aiInstructionsActive: response.data, areAiInstructionsLoading: false },
      })
    } else {
      yield put({ type: actions.SET_STATE, payload: { areAiInstructionsLoading: false } })
      notifyApiError(response?.error, 'Failed to load the instruction')
    }
  } catch (e) {
    yield put({ type: actions.SET_STATE, payload: { areAiInstructionsLoading: false } })
    window.triggerNotification('Failed to load the instruction', 'error')
  }
}

export function* ADD_AI_INSTRUCTION({ payload }: Saga<AiInstructionAddPayload>) {
  yield put({ type: actions.SET_STATE, payload: { areAiInstructionsLoading: true } })
  try {
    const { activeApp, apiHost } = appScope()
    const response: ItemEnvelope<AiInstruction> = yield call(
      aiInstructionsService.add,
      payload,
      activeApp,
      apiHost,
    )

    if (response && response.data) {
      yield put({
        type: actions.AI_INSTRUCTIONS_LIST,
        payload: { currentPage: 1, data: activeApp },
      })
      window.triggerNotification('Instruction added', 'success')
      return response
    }
    yield put({ type: actions.SET_STATE, payload: { areAiInstructionsLoading: false } })
    notifyApiError(response?.error)
    return response
  } catch (e) {
    yield put({ type: actions.SET_STATE, payload: { areAiInstructionsLoading: false } })
    window.triggerNotification('Some error occurred', 'error')
    return null
  }
}

export function* UPDATE_AI_INSTRUCTION({ id, payload }: Write<AiInstructionUpdatePayload>) {
  yield put({ type: actions.SET_STATE, payload: { areAiInstructionsLoading: true } })
  try {
    const { activeApp, apiHost } = appScope()
    const response: ItemEnvelope<AiInstruction> = yield call(
      aiInstructionsService.update,
      id,
      buildUpdatePayload(payload),
      activeApp,
      apiHost,
    )

    if (response && response.data) {
      yield put({ type: actions.SET_STATE, payload: { aiInstructionsActive: response.data } })
      yield put({
        type: actions.AI_INSTRUCTIONS_LIST,
        payload: { currentPage: 1, data: activeApp },
      })
      window.triggerNotification('Instruction saved', 'success')
      return response
    }
    yield put({ type: actions.SET_STATE, payload: { areAiInstructionsLoading: false } })
    notifyApiError(response?.error)
    return response
  } catch (e) {
    yield put({ type: actions.SET_STATE, payload: { areAiInstructionsLoading: false } })
    window.triggerNotification('Some error occurred', 'error')
    return null
  }
}

export function* DELETE_AI_INSTRUCTION({ payload }: Saga<IdPayload>) {
  yield put({ type: actions.SET_STATE, payload: { areAiInstructionsLoading: true } })
  try {
    const { activeApp, apiHost } = appScope()
    const { aiInstructionsPagination } = reduxStore.getState().customBots
    const response: ItemEnvelope<unknown> = yield call(
      aiInstructionsService.del,
      payload.id,
      activeApp,
      apiHost,
    )

    if (response && response.data) {
      yield put({
        type: actions.AI_INSTRUCTIONS_LIST,
        payload: { currentPage: pageAfterDelete(aiInstructionsPagination), data: activeApp },
      })
      window.triggerNotification('Instruction deleted', 'success')
      return
    }
    yield put({ type: actions.SET_STATE, payload: { areAiInstructionsLoading: false } })
    notifyApiError(response?.error)
  } catch (e) {
    yield put({ type: actions.SET_STATE, payload: { areAiInstructionsLoading: false } })
    window.triggerNotification('Some error occurred', 'error')
  }
}

export default function* rootSaga() {
  yield all([
    takeEvery(actions.CUSTOM_BOTS_LIST, CUSTOM_BOTS_LIST as SagaWorker),
    takeEvery(actions.GET_CUSTOM_BOT, GET_CUSTOM_BOT as SagaWorker),
    takeEvery(actions.ADD_CUSTOM_BOT, withCallback(ADD_CUSTOM_BOT) as SagaWorker),
    takeEvery(actions.UPDATE_CUSTOM_BOT, withCallback(UPDATE_CUSTOM_BOT) as SagaWorker),
    takeEvery(actions.DELETE_CUSTOM_BOT, DELETE_CUSTOM_BOT as SagaWorker),
    takeEvery(actions.CHANGE_CUSTOM_BOT_STATUS, CHANGE_CUSTOM_BOT_STATUS as SagaWorker),

    takeEvery(actions.AI_BOTS_LIST, AI_BOTS_LIST as SagaWorker),
    takeEvery(actions.GET_AI_BOT, GET_AI_BOT as SagaWorker),
    takeEvery(actions.ADD_AI_BOT, withCallback(ADD_AI_BOT) as SagaWorker),
    takeEvery(actions.UPDATE_AI_BOT, withCallback(UPDATE_AI_BOT) as SagaWorker),
    takeEvery(actions.DELETE_AI_BOT, DELETE_AI_BOT as SagaWorker),
    takeEvery(actions.CHANGE_AI_BOT_STATUS, CHANGE_AI_BOT_STATUS as SagaWorker),

    takeEvery(actions.AI_INSTRUCTIONS_LIST, AI_INSTRUCTIONS_LIST as SagaWorker),
    takeEvery(actions.GET_AI_INSTRUCTION, GET_AI_INSTRUCTION as SagaWorker),
    takeEvery(actions.ADD_AI_INSTRUCTION, withCallback(ADD_AI_INSTRUCTION) as SagaWorker),
    takeEvery(actions.UPDATE_AI_INSTRUCTION, withCallback(UPDATE_AI_INSTRUCTION) as SagaWorker),
    takeEvery(actions.DELETE_AI_INSTRUCTION, DELETE_AI_INSTRUCTION as SagaWorker),
    takeEvery(actions.AI_INSTRUCTION_OPTIONS, AI_INSTRUCTION_OPTIONS as SagaWorker),
  ])
}
